import { ReactComponent as TypeIcon } from 'assets/icons/merge_type.svg';
import { ReactComponent as FamilyIcon } from 'assets/icons/apartment.svg';
import { ReactComponent as CriticalityIcon } from 'assets/icons/track-level-icon.svg';
import {
  PARAMS,
  PAGE_WHITELISTED_PARAMS,
  SUCCESSION_KEY_JOBS_DEFAULT_PARAMS,
} from 'constants/pages';
import { COVERAGE_STATUS, KEY_JOB_TYPES } from 'constants/succession';
import { isArray, prepareFilterWithAllOption } from 'utility/helpers';
import { parseQueryParams } from 'utility/uiUtils';

const { SEARCH, JOB_TYPE, FAMILY, CRITICALITY, PAGE } = PARAMS;

export const INITIAL_KEY_JOB_FILTERS = {
  [JOB_TYPE]: [],
  [FAMILY]: [],
  [CRITICALITY]: [],
};

const textColumn = (
  id,
  title,
  rowKey,
  minWidth,
  maxWidth = '1fr',
  isHighlighted = false,
  hasConditionalTooltip = false
) => ({
  id,
  title,
  rowKey,
  isSortingDisabled: true,
  minWidth,
  maxWidth,
  isHighlighted,
  hasConditionalTooltip,
});

export const prepareFiltersFromLocation = search => {
  const params = parseQueryParams(
    search || '',
    PAGE_WHITELISTED_PARAMS.SUCCESSION_KEY_JOBS
  );

  return {
    search: params[SEARCH] ? decodeURIComponent(params[SEARCH]) : '',
    page: params[PAGE] || SUCCESSION_KEY_JOBS_DEFAULT_PARAMS[PAGE],
    filters: {
      [JOB_TYPE]: params[JOB_TYPE]
        ? [
            ...(isArray(params[JOB_TYPE])
              ? params[JOB_TYPE]
              : [params[JOB_TYPE]]),
          ]
        : [],
      [FAMILY]: params[FAMILY]
        ? [...(isArray(params[FAMILY]) ? params[FAMILY] : [params[FAMILY]])]
        : [],
      [CRITICALITY]: params[CRITICALITY]
        ? [
            ...(isArray(params[CRITICALITY])
              ? params[CRITICALITY]
              : [params[CRITICALITY]]),
          ]
        : [],
    },
  };
};

export const prepareKeyJobFilters = (labels, settings) => {
  const families = [...(settings?.job_families || [])].reduce((acc, family) => {
    if (family.is_active) {
      acc.push({ id: family.id, name: family.name });
    }
    return acc;
  }, []);

  const criticality = [...(settings?.criticality_levels || [])].reduce(
    (acc, level) => {
      if (level.is_active) {
        acc.push({ id: level.id, name: level.label });
      }
      return acc;
    },
    []
  );

  return [
    {
      id: JOB_TYPE,
      name: labels.type,
      icon: TypeIcon,
      isSingleSelect: true,
      items: prepareFilterWithAllOption(labels.all, [
        { id: KEY_JOB_TYPES.SEAT, name: labels.seat },
        { id: KEY_JOB_TYPES.GENERIC, name: labels.generic },
      ]),
    },
    {
      id: FAMILY,
      name: labels.family,
      icon: FamilyIcon,
      isSingleSelect: true,
      items: prepareFilterWithAllOption(labels.all, families),
    },
    {
      id: CRITICALITY,
      name: labels.criticality,
      icon: CriticalityIcon,
      isSingleSelect: true,
      items: prepareFilterWithAllOption(labels.all, criticality),
    },
  ];
};

export const prepareKeyJobTableHeaders = labels => {
  return [
    textColumn('job', labels.job, 'job', 180, '1fr', true, true),
    textColumn('type', labels.type, 'type', 100, 140),
    textColumn('family', labels.family, 'family', 140, '1fr', false, true),
    textColumn(
      'criticality',
      labels.criticality,
      'criticality',
      120,
      180,
      false,
      true
    ),
    textColumn('holders', labels.holders, 'holders', 90, 120),
    textColumn(
      'readiness',
      labels.readiness,
      'readiness',
      180,
      '1fr',
      false,
      true
    ),
    textColumn('coverage', labels.coverage, 'coverage', 110, 140, false, true),
  ];
};

export const typeLabel = (type, labels) => {
  if (type === KEY_JOB_TYPES.SEAT) return labels.seat;
  if (type === KEY_JOB_TYPES.GENERIC) return labels.generic;
  return type || labels.empty;
};

export const coverageLabel = (status, labels) => {
  if (status === COVERAGE_STATUS.COVERED) return labels.covered;
  if (status === COVERAGE_STATUS.PARTIAL) return labels.partial;
  if (status === COVERAGE_STATUS.GAP) return labels.gap;
  return labels.empty;
};

const prepareReadinessCountsLabel = readinessCounts =>
  readinessCounts.map(readiness => readiness.count).join(' · ');

export const prepareKeyJobTableRows = (jobs, labels) =>
  (jobs || []).map(job => ({
    id: job.id,
    job: job?.name || '-',
    type: labels?.types?.[job?.job_type] || '-',
    family: job?.job_family?.name || '-',
    criticality: job.criticality?.label || '-',
    holders: job?.holders_count || 0,
    readiness: prepareReadinessCountsLabel(job.readiness_counts) || '-',
    coverage: labels.coverage?.[COVERAGE_STATUS[job?.coverage]] || '-',
    name: job?.name || '-',
    rowLink: {
      to: `/succession/key-jobs/${job.id}`,
      state: { title: job?.name || '' },
    },
  }));
